import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { initializeApp, getApp, getApps } from "firebase/app";
import {
  collection,
  getDocs,
  setDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { FaTrash, FaSearch } from "react-icons/fa";
import { db, firebaseConfig } from "../../Firebase/firebaseconfig";
import { useUserContext } from "./UserContext";
import {
  DEFAULT_ROLE_DEFINITIONS,
  PERMISSION_GROUPS,
  ROLE_OPTIONS,
  getDefaultRoleDefinition,
  getRolePermissionCaps,
} from "../../helpers/permissionsCatalog";
import { buildRolePermissions } from "../../helpers/permisos";
import { registrarAuditoria } from "../../helpers/auditoria";
import { dialogService } from "../common/dialogService";
import notify from "../common/notify";
import { EstadoCarga, EstadoVacio, EstadoError } from "../common/EstadoVista";
import { createComisionista, updateComisionista } from "../../services/comisionistasService";
import "./GestionDeUsuarios.css";

const emptyForm = {
  nombre: "",
  correo: "",
  password: "",
  rol: "vendedor",
};

const getSecondaryAuth = () => {
  const appName = "user-creation";
  const secondaryApp = getApps().some((app) => app.name === appName)
    ? getApp(appName)
    : initializeApp(firebaseConfig, appName);

  return getAuth(secondaryApp);
};

const getRoleLabel = (value) =>
  ROLE_OPTIONS.find((r) => r.value === value)?.label || value || "—";

const buildRolesFromSnapshot = (snap) => {
  const firestoreRoles = {};

  snap.docs.forEach((roleDoc) => {
    firestoreRoles[roleDoc.id] = {
      id: roleDoc.id,
      ...roleDoc.data(),
    };
  });

  return ROLE_OPTIONS.reduce((acc, role) => {
    const defaultRole = getDefaultRoleDefinition(role.value) || {};
    const firestoreRole = firestoreRoles[role.value] || {};

    acc[role.value] = {
      id: role.value,
      nombre: firestoreRole.nombre || defaultRole.nombre || role.label,
      descripcion: firestoreRole.descripcion || defaultRole.descripcion || "",
      protected: defaultRole.protected === true || firestoreRole.protected === true,
      permisos: buildRolePermissions(role.value, firestoreRole),
    };

    return acc;
  }, {});
};

export default function GestionUsuarios() {
  const { user, can } = useUserContext();

  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState({});
  const [selectedRole, setSelectedRole] = useState("ejecutivo1");
  const [form, setForm] = useState(emptyForm);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [errorUsuarios, setErrorUsuarios] = useState(false);
  const [savingUsuario, setSavingUsuario] = useState(false);
  const [savingRol, setSavingRol] = useState(false);

  // B: búsqueda y filtro de usuarios
  const [busqueda, setBusqueda] = useState("");
  const [filtroRol, setFiltroRol] = useState("todos");

  const isAdmin = user?.rol === "admin";
  const canViewUsers = can("usuarios.ver") || isAdmin || user?.rol === "ejecutivo1";
  const canCreateUsers = can("usuarios.crear") || isAdmin || user?.rol === "ejecutivo1";
  const canChangeRoles = can("usuarios.cambiarRol") || isAdmin || user?.rol === "ejecutivo1";
  const canEditPermissions =
    can("usuarios.editarPermisos") || isAdmin || user?.rol === "ejecutivo1";
  const canDeleteUsers = can("usuarios.eliminar") || isAdmin;

  const selectedRoleData = useMemo(
    () =>
      roles[selectedRole] || {
        permisos: DEFAULT_ROLE_DEFINITIONS[selectedRole]?.permisos || {},
      },
    [roles, selectedRole]
  );

  const roleIsProtected = selectedRoleData?.protected === true;
  const selectedRoleCaps = useMemo(
    () => getRolePermissionCaps(selectedRole),
    [selectedRole]
  );

  const cargarUsuarios = useCallback(async () => {
    setLoadingUsuarios(true);
    setErrorUsuarios(false);

    try {
      const snap = await getDocs(collection(db, "usuarios"));
      setUsuarios(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error al cargar usuarios:", err);
      setErrorUsuarios(true);
      notify.error("No se pudieron cargar los usuarios.");
    } finally {
      setLoadingUsuarios(false);
    }
  }, []);

  const cargarRoles = useCallback(async () => {
    setLoadingRoles(true);

    try {
      const snap = await getDocs(collection(db, "roles"));
      setRoles(buildRolesFromSnapshot(snap));
    } catch (err) {
      console.error("Error al cargar roles:", err);
      setRoles(
        ROLE_OPTIONS.reduce((acc, role) => {
          const def = getDefaultRoleDefinition(role.value);
          acc[role.value] = {
            id: role.value,
            nombre: def?.nombre || role.label,
            descripcion: def?.descripcion || "",
            protected: def?.protected === true,
            permisos: def?.permisos || {},
          };
          return acc;
        }, {})
      );
      notify.error("No se pudieron cargar roles desde Firestore; usando permisos base.");
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  useEffect(() => {
    if (!canViewUsers) return;

    cargarUsuarios();
    cargarRoles();
  }, [canViewUsers, cargarRoles, cargarUsuarios]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAlta = async (e) => {
    e.preventDefault();
    if (!canCreateUsers || savingUsuario) return;

    setSavingUsuario(true);

    try {
      const secondaryAuth = getSecondaryAuth();
      const cred = await createUserWithEmailAndPassword(
        secondaryAuth,
        form.correo,
        form.password
      );
      await signOut(secondaryAuth);

      await setDoc(doc(db, "usuarios", cred.user.uid), {
        nombre: form.nombre,
        correo: form.correo,
        rol: form.rol,
      });

      await registrarAuditoria({
        accion: "usuarios.crear",
        entidadTipo: "usuario",
        entidadId: cred.user.uid,
        despues: { nombre: form.nombre, correo: form.correo, rol: form.rol },
      });

      // Si el rol es comisionista, crear automáticamente el doc en /comisionistas
      if (form.rol === "comisionista") {
        createComisionista({
          nombre: form.nombre.trim(),
          apellidos: "",
          email: form.correo,
          uid: cred.user.uid,
          tipo: "externo",
          activo: true,
        })
          .then(() => {
            notify.success("Usuario creado. Comisionista generado automáticamente.");
          })
          .catch((err) => {
            console.warn("No se pudo crear el comisionista espejo:", err);
            notify.error(
              "Usuario creado, pero no se pudo generar el comisionista — créalo manualmente."
            );
          });
      } else {
        notify.success("Usuario creado correctamente.");
      }

      setForm(emptyForm);
      await cargarUsuarios();
    } catch (err) {
      console.error(err);
      const code = err?.code || "";
      if (code === "auth/email-already-in-use") {
        notify.error("El correo ya está registrado en Firebase Auth.");
      } else if (code === "auth/invalid-email") {
        notify.error("El formato del correo no es válido.");
      } else if (code === "auth/weak-password") {
        notify.error("La contraseña debe tener al menos 6 caracteres.");
      } else {
        notify.error("Error al dar de alta usuario.");
      }
    } finally {
      setSavingUsuario(false);
    }
  };

  const handleCambioRol = async (uid, nuevoRol) => {
    if (!canChangeRoles) return;

    try {
      await updateDoc(doc(db, "usuarios", uid), { rol: nuevoRol });
      await registrarAuditoria({
        accion: "usuarios.cambiarRol",
        entidadTipo: "usuario",
        entidadId: uid,
        despues: { rol: nuevoRol },
      });
      notify.success(`Rol actualizado a ${getRoleLabel(nuevoRol)}.`);
      await cargarUsuarios();
    } catch (err) {
      console.error("Error al cambiar rol:", err);
      notify.error("No se pudo cambiar el rol.");
    }
  };

  const handleEliminar = async (uid, nombre, correo) => {
    if (!canDeleteUsers) return;
    if (uid === user?.uid) {
      notify.error("No puedes eliminar tu propia cuenta.");
      return;
    }
    const ok = await dialogService.confirm(
      "Se eliminará su acceso al sistema de forma inmediata. La cuenta de Firebase Auth seguirá existiendo (debe borrarse manualmente desde la consola si es necesario).",
      {
        titulo: `¿Eliminar a ${nombre || correo}?`,
        peligro: true,
        confirmText: "Eliminar usuario",
      }
    );
    if (!ok) return;

    try {
      // Buscar si el usuario tiene un comisionista vinculado (por uid) antes de borrar el doc
      const usuarioEliminado = usuarios.find((u) => u.id === uid);
      await deleteDoc(doc(db, "usuarios", uid));

      // Si era comisionista, desactivar su doc en /comisionistas para evitar huérfanos
      if (usuarioEliminado?.rol === "comisionista") {
        getDocs(query(collection(db, "comisionistas"), where("uid", "==", uid)))
          .then((snap) =>
            snap.docs.forEach((d) =>
              updateComisionista(d.id, { activo: false, uid: null }).catch(() => {})
            )
          )
          .catch(() => {});
      }

      await registrarAuditoria({
        accion: "usuarios.eliminar",
        entidadTipo: "usuario",
        entidadId: uid,
        despues: { nombre, correo, rol: usuarioEliminado?.rol, eliminadoPor: user?.uid || "" },
      });
      notify.success(
        `Usuario "${nombre || correo}" eliminado.${
          usuarioEliminado?.rol === "comisionista"
            ? " Comisionista desactivado automáticamente."
            : ""
        }`
      );
      await cargarUsuarios();
    } catch (err) {
      console.error("Error al eliminar usuario:", err);
      notify.error("No se pudo eliminar el usuario.");
    }
  };

  const handleTogglePermiso = (permiso) => {
    if (!canEditPermissions || roleIsProtected) return;
    if (selectedRoleCaps[permiso] !== true) return;

    setRoles((prev) => ({
      ...prev,
      [selectedRole]: {
        ...prev[selectedRole],
        permisos: {
          ...(prev[selectedRole]?.permisos || {}),
          [permiso]: !prev[selectedRole]?.permisos?.[permiso],
        },
      },
    }));
  };

  const guardarPermisosRol = async () => {
    if (!canEditPermissions || roleIsProtected || savingRol) return;

    setSavingRol(true);

    try {
      const roleOption = ROLE_OPTIONS.find((r) => r.value === selectedRole);
      const payload = {
        nombre: selectedRoleData.nombre || roleOption?.label || selectedRole,
        descripcion: selectedRoleData.descripcion || "",
        permisos: buildRolePermissions(selectedRole, selectedRoleData),
        actualizadoEn: serverTimestamp(),
        actualizadoPor: user?.uid || "",
      };

      await setDoc(doc(db, "roles", selectedRole), payload, { merge: true });
      await registrarAuditoria({
        accion: "usuarios.editarPermisos",
        entidadTipo: "rol",
        entidadId: selectedRole,
        despues: payload.permisos,
      });
      notify.success("Permisos guardados correctamente.");
      await cargarRoles();
    } catch (err) {
      console.error("Error guardando permisos:", err);
      notify.error("No se pudieron guardar los permisos.");
    } finally {
      setSavingRol(false);
    }
  };

  const restaurarRolesBase = async () => {
    if (!canEditPermissions || savingRol) return;

    const ok = await dialogService.confirm(
      "Se sobrescribirán los permisos de TODOS los roles con la definición base. Los ajustes personalizados se perderán.",
      { titulo: "¿Restaurar roles base?", peligro: true, confirmText: "Restaurar" }
    );
    if (!ok) return;

    setSavingRol(true);

    try {
      await Promise.all(
        ROLE_OPTIONS.map((role) => {
          const def = getDefaultRoleDefinition(role.value);
          if (!def) return Promise.resolve();

          return setDoc(
            doc(db, "roles", role.value),
            {
              nombre: def.nombre || role.label,
              descripcion: def.descripcion || "",
              protected: def.protected === true,
              permisos: def.permisos || {},
              actualizadoEn: serverTimestamp(),
              actualizadoPor: user?.uid || "",
            },
            { merge: true }
          );
        })
      );

      await registrarAuditoria({
        accion: "usuarios.restaurarPermisosBase",
        entidadTipo: "roles",
        entidadId: "todos",
      });
      notify.success("Roles base restaurados correctamente.");
      await cargarRoles();
    } catch (err) {
      console.error("Error restaurando roles base:", err);
      notify.error("No se pudieron restaurar los roles base.");
    } finally {
      setSavingRol(false);
    }
  };

  const permisosActivos = useMemo(() => {
    return Object.values(selectedRoleData?.permisos || {}).filter(Boolean).length;
  }, [selectedRoleData]);

  const usuariosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return usuarios.filter((u) => {
      if (filtroRol !== "todos" && (u.rol || "") !== filtroRol) return false;
      if (!q) return true;
      return (
        (u.nombre || "").toLowerCase().includes(q) ||
        (u.correo || "").toLowerCase().includes(q)
      );
    });
  }, [usuarios, busqueda, filtroRol]);

  const hayFiltro = busqueda.trim() !== "" || filtroRol !== "todos";

  if (!canViewUsers) return null;

  const renderTablaUsuarios = () => {
    if (loadingUsuarios && usuarios.length === 0) {
      return <EstadoCarga texto="Cargando usuarios…" />;
    }
    if (errorUsuarios && usuarios.length === 0) {
      return <EstadoError onReintentar={cargarUsuarios} />;
    }
    if (usuariosFiltrados.length === 0) {
      return hayFiltro ? (
        <EstadoVacio
          icono="🔎"
          titulo="Sin coincidencias"
          detalle="Ningún usuario coincide con la búsqueda o el filtro de rol."
        />
      ) : (
        <EstadoVacio titulo="Sin usuarios cargados" detalle="Da de alta el primer usuario arriba." />
      );
    }

    return (
      <div className="table-wrapper">
        <table className="usuarios-table">
          <thead>
            <tr>
              <th scope="col">Nombre</th>
              <th scope="col">Correo</th>
              <th scope="col">Rol</th>
              {canDeleteUsers && <th scope="col" className="col-acciones">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {usuariosFiltrados.map((u) => (
              <tr key={u.id} className={u.id === user?.uid ? "row-self" : ""}>
                <td data-label="Nombre">
                  {u.nombre || "—"}
                  {u.id === user?.uid && <span className="badge-tu">tú</span>}
                </td>
                <td data-label="Correo">{u.correo || "—"}</td>
                <td data-label="Rol">
                  <select
                    aria-label={`Rol de ${u.nombre || u.correo}`}
                    value={u.rol || "vendedor"}
                    onChange={(e) => handleCambioRol(u.id, e.target.value)}
                    disabled={!canChangeRoles}
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </td>
                {canDeleteUsers && (
                  <td data-label="Acciones" className="col-acciones">
                    <button
                      type="button"
                      className="btn-eliminar-usuario"
                      title={
                        u.id === user?.uid
                          ? "No puedes eliminar tu propia cuenta"
                          : `Eliminar a ${u.nombre || u.correo}`
                      }
                      aria-label={`Eliminar a ${u.nombre || u.correo}`}
                      disabled={u.id === user?.uid}
                      onClick={() => handleEliminar(u.id, u.nombre, u.correo)}
                    >
                      <FaTrash size={13} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="gestion-usuarios">
      <div className="gestion-header">
        <div>
          <p>Administración</p>
          <h2>Gestión de usuarios</h2>
          <span>Roles, usuarios y permisos por capacidad.</span>
        </div>
        <div className="gestion-stats">
          <div className="gestion-stat">
            <b>{usuarios.length}</b>
            <span>Usuarios</span>
          </div>
          <div className="gestion-stat">
            <b>{ROLE_OPTIONS.length}</b>
            <span>Roles</span>
          </div>
        </div>
      </div>

      <section className="gestion-panel">
        <div className="panel-head">
          <h3>Alta de usuario</h3>
          {!canCreateUsers && <span>Sin permiso para crear usuarios</span>}
        </div>

        <form onSubmit={handleAlta} className="alta-usuario-form">
          <input
            name="nombre"
            aria-label="Nombre completo"
            value={form.nombre}
            onChange={handleChange}
            placeholder="Nombre completo"
            required
            disabled={!canCreateUsers}
          />
          <input
            name="correo"
            type="email"
            aria-label="Correo"
            value={form.correo}
            onChange={handleChange}
            placeholder="Correo"
            required
            disabled={!canCreateUsers}
          />
          <input
            name="password"
            type="password"
            aria-label="Contraseña"
            value={form.password}
            onChange={handleChange}
            placeholder="Contraseña"
            required
            disabled={!canCreateUsers}
          />
          <select
            name="rol"
            aria-label="Rol del nuevo usuario"
            value={form.rol}
            onChange={handleChange}
            disabled={!canCreateUsers}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <button type="submit" disabled={!canCreateUsers || savingUsuario}>
            {savingUsuario ? "Guardando…" : "Dar de alta"}
          </button>
        </form>
      </section>

      <section className="gestion-grid">
        <div className="gestion-panel">
          <div className="panel-head">
            <div>
              <h3>Usuarios</h3>
              <span>
                {hayFiltro
                  ? `${usuariosFiltrados.length} de ${usuarios.length}`
                  : `${usuarios.length} en total`}
              </span>
            </div>
          </div>

          <div className="gestion-toolbar">
            <div className="gestion-search">
              <FaSearch aria-hidden="true" />
              <input
                type="search"
                aria-label="Buscar usuario por nombre o correo"
                placeholder="Buscar por nombre o correo…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
            <select
              aria-label="Filtrar por rol"
              className="gestion-filtro-rol"
              value={filtroRol}
              onChange={(e) => setFiltroRol(e.target.value)}
            >
              <option value="todos">Todos los roles</option>
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            {hayFiltro && (
              <button
                type="button"
                className="gestion-limpiar"
                onClick={() => {
                  setBusqueda("");
                  setFiltroRol("todos");
                }}
              >
                Limpiar
              </button>
            )}
          </div>

          {renderTablaUsuarios()}
        </div>

        <div className="gestion-panel permisos-panel">
          <div className="panel-head">
            <div>
              <h3>Roles y permisos</h3>
              <span>{permisosActivos} permisos activos</span>
            </div>

            {loadingRoles && <span>Cargando…</span>}
          </div>

          <div className="roles-tools">
            <button
              type="button"
              onClick={restaurarRolesBase}
              disabled={!canEditPermissions || savingRol}
            >
              Restaurar roles base
            </button>
          </div>

          <div className="role-selector">
            {ROLE_OPTIONS.map((role) => (
              <button
                type="button"
                key={role.value}
                className={selectedRole === role.value ? "active" : ""}
                onClick={() => setSelectedRole(role.value)}
              >
                {role.label}
              </button>
            ))}
          </div>

          {roleIsProtected && (
            <div className="protected-note">
              El rol administrador tiene todos los permisos y no se edita desde
              esta pantalla.
            </div>
          )}

          <div className="permission-groups">
            {PERMISSION_GROUPS.map((group) => (
              <div key={group.key} className="permission-group">
                <h4>{group.label}</h4>

                {group.permissions.map((permission) => (
                  <label key={permission.key} className="permission-row">
                    <input
                      type="checkbox"
                      checked={selectedRoleData?.permisos?.[permission.key] === true}
                      onChange={() => handleTogglePermiso(permission.key)}
                      disabled={
                        !canEditPermissions ||
                        roleIsProtected ||
                        selectedRoleCaps[permission.key] !== true
                      }
                    />
                    <span>{permission.label}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>

          <div className="permissions-actions">
            <button
              type="button"
              onClick={guardarPermisosRol}
              disabled={!canEditPermissions || roleIsProtected || savingRol}
            >
              {savingRol ? "Guardando…" : "Guardar permisos"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
